import { sumVal, avgVal, lastVal, trendPct, fmtInt, toNum } from "./chartHelpers";
import { formatDangerousEventType, attrsToMap, resolveEventUser } from "./dangerousEventTypes";
import { buildDashboardPeriodContext } from "./insightsPeriod";

const HIGH_EVENT_SUM_THRESHOLD = 3000;

const SEVERITY_ORDER = { danger: 0, warning: 1, info: 2, success: 3 };

function insight(id, severity, title, message, link = null, linkLabel = "Подробнее") {
  return { id, severity, title, message, link, linkLabel };
}

function sortInsights(list) {
  return [...list].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

function avgNumArr(arr) {
  const v = avgVal(arr);
  return v != null ? v : null;
}

function maxShare(names, sums) {
  const total = sumVal(sums) || 0;
  if (!total || !Array.isArray(names)) return null;
  let best = { name: "", pct: 0, sum: 0 };
  names.forEach((name, i) => {
    const s = toNum(sums[i]) || 0;
    const pct = (s / total) * 100;
    if (pct > best.pct) best = { name: name || "—", pct, sum: s };
  });
  return best;
}

function closedDayPhrase(ctx) {
  return ctx.excludeLastDay ? "последний закрытый день" : "последний день";
}

/** Подсказки для главного дашборда (данные DataStore). */
export function buildDashboardInsights(store, { shifts = [] } = {}) {
  const insights = [];
  if (!store || !store.openDate?.length) return insights;

  const ctx = buildDashboardPeriodContext(store, shifts);
  const dayWord = closedDayPhrase(ctx);

  if (ctx.excludeLastDay && ctx.excludedDateLabel) {
    insights.push(
      insight(
        "open_day_excluded",
        "info",
        "Незакрытый торговый день",
        `День ${ctx.excludedDateLabel} не учтён в дневной аналитике — кассовая смена ещё не закрыта.`,
        "/cash-shifts",
        "Кассовые смены",
      ),
    );
  }

  const revenue = ctx.trim(store.dishDiscountSumInt || []);
  const returns = ctx.trim(store.dishReturnSum || []);
  const orders = ctx.trim(store.uniqOrderId || []);
  const guests = ctx.trim(store.guestNum || []);
  const avgCheck = ctx.trim(store.dishDiscountSumIntAverage || []);
  const foodCost = ctx.trim(store.productCostBasePercent || []);
  const items = ctx.trim(store.orderItems || []);

  const totalRevenue = sumVal(revenue) || 0;
  const totalReturns = sumVal(returns) || 0;
  const totalOrders = sumVal(orders) || 0;
  const totalItems = sumVal(items) || 0;
  const returnPct = totalRevenue > 0 ? (totalReturns / totalRevenue) * 100 : 0;
  const itemsPerCheck = totalOrders > 0 ? totalItems / totalOrders : null;

  const revTrend = trendPct(revenue);
  if (revTrend != null && revTrend <= -15) {
    insights.push(
      insight(
        "revenue_drop",
        "warning",
        "Просадка выручки",
        `Выручка за ${dayWord} ниже предыдущего на ${Math.abs(revTrend).toFixed(1)}%. Сверьте смены и загрузку зала.`,
        "/dishdetails",
      ),
    );
  } else if (revTrend != null && revTrend >= 15) {
    insights.push(
      insight(
        "revenue_up",
        "success",
        "Рост выручки",
        `Выручка за ${dayWord} выше предыдущего на ${revTrend.toFixed(1)}%.`,
        "/dishdetails",
      ),
    );
  }

  if (returnPct >= 5) {
    insights.push(
      insight(
        "returns_critical",
        "danger",
        "Высокий уровень возвратов",
        `Возвраты составляют ${returnPct.toFixed(1)}% от выручки (${fmtInt(totalReturns)} ₽). Проверьте опасные операции и кассовые смены.`,
        "/ops-summary",
        "Операционный контроль",
      ),
    );
  } else if (returnPct >= 2) {
    insights.push(
      insight(
        "returns_elevated",
        "warning",
        "Повышенные возвраты",
        `Доля возвратов ${returnPct.toFixed(1)}% (${fmtInt(totalReturns)} ₽) — выше обычного порога 2%.`,
        "/returndetails",
      ),
    );
  }

  const retTrend = trendPct(returns);
  const lastReturn = lastVal(returns);
  if (retTrend != null && retTrend >= 50 && lastReturn > 0) {
    insights.push(
      insight(
        "returns_spike",
        "warning",
        "Скачок возвратов",
        `Сумма возвратов за ${dayWord} выросла на ${retTrend.toFixed(0)}% к предыдущему дню.`,
        "/dangerous-ops",
        "Опасные операции",
      ),
    );
  }

  const avgFood = avgNumArr(foodCost.map((v) => toNum(v)));
  if (avgFood != null && avgFood >= 38) {
    insights.push(
      insight(
        "food_cost_high",
        "warning",
        "Высокая себестоимость",
        `Средняя себестоимость за период ${avgFood.toFixed(1)}%. Проверьте закупочные цены и списания.`,
        "/costdetails",
      ),
    );
  }

  const periodAvgCheck = avgNumArr(avgCheck);
  const lastAvgCheck = lastVal(avgCheck);
  if (periodAvgCheck != null && lastAvgCheck != null && periodAvgCheck > 0 && lastAvgCheck < periodAvgCheck * 0.8) {
    insights.push(
      insight(
        "avg_check_drop",
        "warning",
        "Падение среднего чека",
        `Средний чек в ${dayWord} (${fmtInt(lastAvgCheck)} ₽) заметно ниже среднего за закрытые дни (${fmtInt(periodAvgCheck)} ₽).`,
        "/avgcheckdetails",
      ),
    );
  }

  const payTop = maxShare(store.payTypesName, store.payTypesSum);
  if (payTop && payTop.pct >= 75) {
    insights.push(
      insight(
        "payment_concentration",
        "info",
        "Доминирует один тип оплаты",
        `«${payTop.name}» — ${payTop.pct.toFixed(0)}% всех оплат. Убедитесь, что это ожидаемо для вашего формата.`,
        "/payment-types",
      ),
    );
  }

  const catTop = maxShare(store.categoryName, store.categorySale);
  if (catTop && catTop.pct >= 55) {
    insights.push(
      insight(
        "category_concentration",
        "info",
        "Концентрация продаж",
        `Категория «${catTop.name}» даёт ${catTop.pct.toFixed(0)}% выручки — возможна зависимость от узкого ассортимента.`,
        "/categories",
      ),
    );
  }

  if (itemsPerCheck != null && itemsPerCheck < 1.8) {
    insights.push(
      insight(
        "items_per_check_low",
        "info",
        "Мало позиций в чеке",
        `В среднем ${itemsPerCheck.toFixed(1)} поз. на чек — возможны допродажи или слишком короткое меню в чеке.`,
        "/itemsdetails",
      ),
    );
  }

  const guestTrend = trendPct(guests);
  const orderTrend = trendPct(orders);
  if (
    guestTrend != null &&
    orderTrend != null &&
    guestTrend * orderTrend < 0 &&
    Math.abs(guestTrend) >= 10 &&
    Math.abs(orderTrend) >= 10
  ) {
    insights.push(
      insight(
        "guests_orders_diverge",
        "info",
        "Гости и чеки расходятся",
        `Гости ${guestTrend > 0 ? "↑" : "↓"}${Math.abs(guestTrend).toFixed(0)}%, чеки ${orderTrend > 0 ? "↑" : "↓"}${Math.abs(orderTrend).toFixed(0)}% за ${dayWord} — проверьте размер групп и объединение чеков.`,
        "/guestsdetails",
      ),
    );
  }

  if (revenue.length >= 2) {
    const nums = revenue.map((v) => toNum(v) || 0);
    const labels = ctx.closedDateLabels;
    let minIdx = 0;
    let maxIdx = 0;
    for (let i = 1; i < nums.length; i += 1) {
      if (nums[i] < nums[minIdx]) minIdx = i;
      if (nums[i] > nums[maxIdx]) maxIdx = i;
    }
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    const maxVal = nums[maxIdx];
    const minVal = nums[minIdx];
    const maxLabel = labels[maxIdx] || "—";
    const minLabel = labels[minIdx] || "—";

    if (maxVal > avg * 1.2) {
      insights.push(
        insight(
          "best_day",
          "success",
          "Лучший день периода",
          `${maxLabel} — максимум выручки среди закрытых дней (${fmtInt(maxVal)} ₽, среднее ${fmtInt(avg)} ₽).`,
          "/dishdetails",
        ),
      );
    }
    if (minVal < avg * 0.75) {
      insights.push(
        insight(
          "worst_day",
          "warning",
          "Слабый день",
          `${minLabel} — минимум выручки среди закрытых дней (${fmtInt(minVal)} ₽ при среднем ${fmtInt(avg)} ₽).`,
          "/dishdetails",
        ),
      );
    }
  }

  return sortInsights(insights);
}

function parseEventSum(am) {
  return toNum(am.sum) ?? 0;
}

function getEventHour(dateStr) {
  if (!dateStr) return null;
  const raw = String(dateStr).trim().replace(" ", "T");
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getHours();
}

function topMapEntry(map, minCount = 1) {
  const entries = [...map.entries()].filter(([, c]) => c >= minCount);
  if (!entries.length) return null;
  return entries.sort((a, b) => b[1] - a[1])[0];
}

/** Глубокая аналитика по журналу опасных операций. */
export function analyzeDangerousEvents(events = []) {
  const insights = [];
  if (!events.length) return insights;

  const byType = new Map();
  const byUser = new Map();
  const byWaiter = new Map();
  const byHour = new Map();
  const byOrder = new Map();
  const sumByType = new Map();
  let totalSum = 0;
  let highSumCount = 0;
  let nightCount = 0;

  for (const ev of events) {
    const am = attrsToMap(ev.attributes);
    const typeLabel = formatDangerousEventType(ev);
    const sum = parseEventSum(am);
    totalSum += sum;
    if (sum >= HIGH_EVENT_SUM_THRESHOLD) highSumCount += 1;

    byType.set(typeLabel, (byType.get(typeLabel) || 0) + 1);
    sumByType.set(typeLabel, (sumByType.get(typeLabel) || 0) + sum);

    const user = resolveEventUser(am);
    if (user && String(user).trim() && user !== "—") {
      byUser.set(String(user), (byUser.get(String(user)) || 0) + 1);
    }

    const waiter = am.waiter;
    if (waiter && String(waiter).trim() && waiter !== "—") {
      byWaiter.set(String(waiter), (byWaiter.get(String(waiter)) || 0) + 1);
    }

    const orderNum = am.orderNum;
    if (orderNum && String(orderNum).trim() && orderNum !== "—") {
      const key = String(orderNum);
      byOrder.set(key, (byOrder.get(key) || 0) + 1);
    }

    const hour = getEventHour(ev.date);
    if (hour != null) {
      byHour.set(hour, (byHour.get(hour) || 0) + 1);
      if (hour >= 22 || hour < 6) nightCount += 1;
    }
  }

  const count = events.length;
  const avgSum = count > 0 ? totalSum / count : 0;

  const topType = topMapEntry(byType);
  if (topType) {
    const [typeName, typeCount] = topType;
    const pct = (typeCount / count) * 100;
    const typeSum = sumByType.get(typeName) || 0;
    insights.push(
      insight(
        "ev_top_type",
        pct >= 40 ? "warning" : "info",
        "Лидирующий тип событий",
        `«${typeName}» — ${typeCount} из ${count} (${pct.toFixed(0)}%), на сумму ${fmtInt(typeSum)} ₽.`,
        "/dangerous-ops",
      ),
    );
  }

  const topTypeBySum = [...sumByType.entries()].sort((a, b) => b[1] - a[1])[0];
  if (topTypeBySum && topTypeBySum[1] > 0 && topType?.[0] !== topTypeBySum[0]) {
    insights.push(
      insight(
        "ev_top_type_sum",
        "info",
        "Самый дорогой тип событий",
        `«${topTypeBySum[0]}» — наибольшая сумма: ${fmtInt(topTypeBySum[1])} ₽.`,
        "/dangerous-ops",
      ),
    );
  }

  const topUser = topMapEntry(byUser, 2);
  if (topUser) {
    insights.push(
      insight(
        "ev_top_user",
        topUser[1] >= 8 ? "danger" : topUser[1] >= 5 ? "warning" : "info",
        "Сотрудник в журнале событий",
        `${topUser[0]} — ${topUser[1]} опасных операций (${((topUser[1] / count) * 100).toFixed(0)}% от всех).`,
        "/dangerous-ops",
      ),
    );
  }

  const topWaiter = topMapEntry(byWaiter, 2);
  if (topWaiter) {
    insights.push(
      insight(
        "ev_top_waiter",
        topWaiter[1] >= 4 ? "warning" : "info",
        "Официант в событиях",
        `${topWaiter[0]} фигурирует в ${topWaiter[1]} событиях — проверьте заказы и комментарии.`,
        "/dangerous-ops",
      ),
    );
  }

  const topHour = topMapEntry(byHour, 2);
  if (topHour) {
    const h = topHour[0];
    insights.push(
      insight(
        "ev_peak_hour",
        topHour[1] >= 5 ? "warning" : "info",
        "Пик по времени суток",
        `Больше всего событий в ${String(h).padStart(2, "0")}:00–${String((h + 1) % 24).padStart(2, "0")}:00 (${topHour[1]} шт.). Усильте контроль в этот интервал.`,
        "/dangerous-ops",
      ),
    );
  }

  const repeatOrders = [...byOrder.entries()].filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]);
  if (repeatOrders.length > 0) {
    const [num, c] = repeatOrders[0];
    insights.push(
      insight(
        "ev_repeat_order",
        c >= 3 ? "danger" : "warning",
        "Повторные события по заказу",
        `Заказ №${num} — ${c} событий за период. Возможны множественные правки, удаления или возвраты.`,
        "/dangerous-ops",
      ),
    );
    if (repeatOrders.length > 1) {
      insights.push(
        insight(
          "ev_repeat_orders_many",
          "info",
          "Несколько проблемных заказов",
          `${repeatOrders.length} заказ(ов) с 2 и более событиями. Среди них: №${repeatOrders.slice(0, 3).map(([n]) => n).join(", №")}.`,
          "/dangerous-ops",
        ),
      );
    }
  }

  if (highSumCount > 0) {
    insights.push(
      insight(
        "ev_high_sum",
        highSumCount >= 3 ? "danger" : "warning",
        "Крупные суммы в событиях",
        `${highSumCount} событий от ${fmtInt(HIGH_EVENT_SUM_THRESHOLD)} ₽. Средняя сумма на событие: ${fmtInt(avgSum)} ₽, всего: ${fmtInt(totalSum)} ₽.`,
        "/dangerous-ops",
      ),
    );
  }

  if (nightCount >= 2 && nightCount / count >= 0.25) {
    insights.push(
      insight(
        "ev_night",
        "warning",
        "События в ночное время",
        `${nightCount} из ${count} (${((nightCount / count) * 100).toFixed(0)}%) приходятся на 22:00–06:00.`,
        "/dangerous-ops",
      ),
    );
  }

  if (byType.size >= 4) {
    insights.push(
      insight(
        "ev_types_diverse",
        "info",
        "Разнообразие типов нарушений",
        `Зафиксировано ${byType.size} различных типов — не единичный инцидент, а несколько видов рисков.`,
        "/dangerous-ops",
      ),
    );
  }

  const secondType = [...byType.entries()].sort((a, b) => b[1] - a[1])[1];
  if (topType && secondType && secondType[1] >= 2) {
    const gap = topType[1] - secondType[1];
    if (gap <= 1) {
      insights.push(
        insight(
          "ev_types_balanced",
          "info",
          "Два основных типа риска",
          `Почти поровну: «${topType[0]}» (${topType[1]}) и «${secondType[0]}» (${secondType[1]}). Разберите оба направления.`,
          "/dangerous-ops",
        ),
      );
    }
  }

  return insights;
}

/** Подсказки для операционного контроля. */
export function buildOpsInsights({ evSummary, cashSummary, analytics, events = [] }) {
  const insights = [];
  const evCount = evSummary?.count ?? 0;
  const evSum = evSummary?.totalSum ?? 0;
  const diffCount = cashSummary?.diffCount ?? 0;
  const warningCount = cashSummary?.warningCount ?? 0;
  const totalCashDiff = cashSummary?.totalCashDiff ?? 0;

  if (evCount >= 15) {
    insights.push(
      insight(
        "events_many",
        "danger",
        "Много опасных операций",
        `Зафиксировано ${evCount} событий на сумму ${fmtInt(evSum)} ₽. Рекомендуется разбор с управляющим.`,
        "/dangerous-ops",
      ),
    );
  } else if (evCount >= 5) {
    insights.push(
      insight(
        "events_some",
        "warning",
        "Есть опасные операции",
        `${evCount} событий за период (${fmtInt(evSum)} ₽). Просмотрите журнал iiko.`,
        "/dangerous-ops",
      ),
    );
  }

  if (diffCount >= 3) {
    insights.push(
      insight(
        "cash_diff_many",
        "danger",
        "Расхождения кассы",
        `${diffCount} смен с расхождением, суммарно ${fmtInt(totalCashDiff)} ₽. Проверьте кассиров и инкассацию.`,
        "/cash-analytics",
        "Кассовая аналитика",
      ),
    );
  } else if (diffCount > 0) {
    insights.push(
      insight(
        "cash_diff_some",
        "warning",
        "Расхождение в кассе",
        `${diffCount} смена(ы) с cashDiff. Откройте список смен с фильтром «только с расхождением».`,
        "/cash-shifts",
      ),
    );
  }

  if (warningCount > 0) {
    insights.push(
      insight(
        "shifts_warnings",
        "warning",
        "Подозрительные смены",
        `${warningCount} смен со статусом HASWARNINGS — требуют проверки.`,
        "/cash-shifts",
      ),
    );
  }

  const topDiff = analytics?.diffs?.[0];
  if (topDiff && Math.abs(topDiff.cashDiff) >= 500) {
    const name = topDiff.shift?.responsibleUserName || topDiff.shift?.managerName || "—";
    insights.push(
      insight(
        "top_cash_diff",
        "warning",
        "Крупное расхождение",
        `Смена №${topDiff.shift?.sessionNumber}: расхождение ${fmtInt(topDiff.cashDiff)} ₽ (кассир: ${name}).`,
        "/cash-shifts",
      ),
    );
  }

  if (evCount === 0 && diffCount === 0 && warningCount === 0) {
    insights.push(
      insight(
        "ops_clean",
        "success",
        "Период без критичных сигналов",
        "Опасных операций и расхождений кассы за выбранный период не обнаружено.",
      ),
    );
  }

  if (evCount > 0 && diffCount > 0) {
    insights.push(
      insight(
        "ops_combined_risk",
        "danger",
        "Комбинированный риск",
        "Одновременно есть опасные операции и расхождения кассы — приоритетный разбор для управляющего.",
        "/ops-summary",
      ),
    );
  }

  if (events.length > 0) {
    insights.push(...analyzeDangerousEvents(events));
  }

  return sortInsights(insights);
}

/** Подсказки для сравнения периодов. */
export function buildCompareInsights(comparison = []) {
  const insights = [];
  if (!comparison.length) return insights;

  const revenue = comparison.find((c) => c.key === "revenue");
  if (revenue?.deltaPct != null && revenue.deltaPct <= -10) {
    insights.push(
      insight(
        "compare_revenue_down",
        "warning",
        "Выручка снизилась",
        `Период B ниже A на ${Math.abs(revenue.deltaPct).toFixed(1)}% (${fmtInt(revenue.delta)} ₽). Разберите группы ресторанов и официантов.`,
        "/groups",
        "По группам",
      ),
    );
  } else if (revenue?.deltaPct != null && revenue.deltaPct >= 10) {
    insights.push(
      insight(
        "compare_revenue_up",
        "success",
        "Рост выручки",
        `Период B выше A на ${revenue.deltaPct.toFixed(1)}% (+${fmtInt(revenue.delta)} ₽).`,
        "/compare",
      ),
    );
  }

  const returns = comparison.find((c) => c.key === "returns");
  if (returns?.delta != null && returns.delta > 0 && returns.valA > 0) {
    const pct = (returns.delta / returns.valA) * 100;
    if (pct >= 20) {
      insights.push(
        insight(
          "compare_returns_up",
          "warning",
          "Возвраты выросли",
          `Возвраты в периоде B больше на ${fmtInt(returns.delta)} ₽ (+${pct.toFixed(0)}%).`,
          "/returndetails",
        ),
      );
    }
  }

  const guests = comparison.find((c) => c.key === "guests");
  const avgCheck = comparison.find((c) => c.key === "avgCheck");
  if (
    revenue?.deltaPct != null &&
    revenue.deltaPct < -5 &&
    guests?.deltaPct != null &&
    guests.deltaPct >= 0 &&
    avgCheck?.deltaPct != null &&
    avgCheck.deltaPct < -5
  ) {
    insights.push(
      insight(
        "compare_traffic_ok_check_down",
        "info",
        "Гости есть, чек просел",
        "Гостевой трафик не падает, но средний чек снизился — проверьте допродажи и акции.",
        "/avgcheckdetails",
      ),
    );
  }

  const foodCost = comparison.find((c) => c.key === "foodCost");
  if (foodCost?.delta != null && foodCost.delta >= 2) {
    insights.push(
      insight(
        "compare_food_cost_up",
        "warning",
        "Себестоимость выросла",
        `Средняя себестоимость выше на ${foodCost.delta.toFixed(1)} п.п. — проверьте закупки и техкарты.`,
        "/costdetails",
      ),
    );
  }

  return sortInsights(insights);
}
